import type { PatientSummary } from "@allergy123/contracts/clinical";

export const KIND_LABELS: Record<string, string> = { test: "Test", retest: "Retest", continuation: "Continuation", office_visit: "Office visit", adjustment: "Adjustment" };
export const TEST_KINDS = ["test", "retest", "continuation"] as const;
export const isTestKind = (kind: string) => (TEST_KINDS as readonly string[]).includes(kind);
export const kindLabel = (kind: string) => KIND_LABELS[kind] ?? kind;

export const STATUS_LABELS: Record<string, string> = {
  scheduled: "Scheduled",
  arrived: "Arrived",
  completed: "Completed",
  cancelled: "Cancelled",
  draft: "In progress",
  submitted: "Submitted",
  signed: "Signed",
  corrected: "Corrected",
  queued: "Report queued",
  rendering: "Report rendering",
  ready: "Report ready",
  failed: "Report failed",
};
export const statusLabel = (status: string) => STATUS_LABELS[status] ?? status;

/** Billing plans as the practice reads them (ADR 0008); the codes are the account's `plan`. */
export const PLAN_LABELS: Record<string, string> = {
  alc: "A la Carte",
  bas_m2m: "Base month to month",
  bas: "Base annual",
  cor: "Core",
  cus: "Custom",
};
export const planLabel = (plan: string) => PLAN_LABELS[plan] ?? plan;

/** A supply order's life: placed by the practice, confirmed and charged by staff, shipped with tracking, delivered or cancelled. */
export const ORDER_STATUS_LABELS: Record<string, string> = {
  received: "Received",
  confirmed: "Confirmed",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};
export const orderStatusLabel = (status: string) => ORDER_STATUS_LABELS[status] ?? status;

/** The payment side of an order: nothing is charged until staff confirm it. */
export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  unpaid: "Not yet charged",
  processing: "Payment processing",
  paid: "Paid",
  failed: "Payment failed",
};
export const paymentStatusLabel = (status: string) => PAYMENT_STATUS_LABELS[status] ?? status;

export const fullName = (p: Pick<PatientSummary, "givenName" | "familyName">) => `${p.familyName}, ${p.givenName}`;

/** Local wall time in the location's zone, since every instant on the wire is UTC (Q-31). */
export function formatTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" }).format(new Date(iso));
}
export function formatDateTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone, month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(iso));
}
export function formatDate(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone, month: "short", day: "numeric", year: "numeric" }).format(new Date(iso));
}
/** A calendar date (YYYY-MM-DD) rendered without a zone shift. */
export function formatCalendarDate(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(y!, m! - 1, d!)));
}
/** Today's calendar date in a zone, as YYYY-MM-DD. */
export function todayIn(timeZone: string, now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}
export function shiftDate(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + days)).toISOString().slice(0, 10);
}
export function ageOn(dateOfBirth: string, now = new Date()): number {
  const [y, m, d] = dateOfBirth.split("-").map(Number);
  let age = now.getUTCFullYear() - y!;
  if (now.getUTCMonth() + 1 < m! || (now.getUTCMonth() + 1 === m! && now.getUTCDate() < d!)) age -= 1;
  return age;
}
export const SEX_LABELS: Record<string, string> = { female: "Female", male: "Male", other: "Other", unknown: "Not recorded" };

/** Minutes after local midnight of an instant, in a zone. */
export function localMinutes(iso: string, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(iso));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return get("hour") * 60 + get("minute");
}
/** The local calendar date (YYYY-MM-DD) of an instant, in a zone. */
export function localDate(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
}
/** The UTC instant of a local wall time (`date` plus `minutes` after midnight) in a zone, the way the server computes a day's bounds (Q-76). */
export function zonedToIso(date: string, minutes: number, timeZone: string): string {
  const [y, m, d] = date.split("-").map(Number);
  const guess = Date.UTC(y!, m! - 1, d!, 0, minutes);
  const offsetAt = (at: number) => {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(at));
    const get = (type: string) => Number(parts.find((p) => p.type === type)!.value);
    return (Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute")) - at) / 60_000;
  };
  let utc = guess - offsetAt(guess) * 60_000;
  utc = guess - offsetAt(utc) * 60_000;
  return new Date(utc).toISOString();
}
/** ISO weekday, 1 Monday to 7 Sunday, of a calendar date. */
export function isoWeekday(date: string): number {
  const [y, m, d] = date.split("-").map(Number);
  const day = new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay();
  return day === 0 ? 7 : day;
}
export const hhmm = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
export const minutesOf = (text: string) => {
  const [h, m] = text.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
export const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
